import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { useRBAC } from '../hooks/useRBAC';
import { policyService } from '../services/policyService';
import { Policy, PolicyCreateEntry, PolicyUpdateEntry, PolicyConsent, PolicyQuestion } from 'public-contracts';
import { chatHistoryService, ChatHistory } from '../services/chatHistoryService';
import { assistantService } from '../services/assistantService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Edit,
  Trash2,
  FileText,
  Search,
  Filter,
  Tag,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Download,
  MessageSquare,
  Sparkles,
  Loader2,
  RefreshCw,
  Check,
  X,
  HelpCircle,
  CheckCircle,
} from 'lucide-react';
import { Label } from '../components/ui/label';

const ModernPolicyManagement: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user, fetchUser } = useUserStore();
  const { hasPermission, highestRole } = useRBAC();

  // Permission checks - staff can only view, not manage
  const canCreate = hasPermission('policies', 'create');
  const canEdit = hasPermission('policies', 'edit');
  const canDelete = hasPermission('policies', 'delete');
  const isStaff = highestRole === 'staff';

  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [deletingPolicy, setDeletingPolicy] = useState<Policy | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatHistory | null>(null);
  const [extractingMetadata, setExtractingMetadata] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState<string | null>(null);
  const [originalContent, setOriginalContent] = useState<string>('');
  const [rewriteImprovements, setRewriteImprovements] = useState<string[]>([]);
  
  // Consent and Questions state
  const [consents, setConsents] = useState<Record<string, PolicyConsent>>({});
  const [questions, setQuestions] = useState<Record<string, PolicyQuestion[]>>({});
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questioningPolicy, setQuestioningPolicy] = useState<Policy | null>(null);
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<'AI' | 'MANAGER'>('MANAGER');
  const [askingAI, setAskingAI] = useState(false);
  const [consenting, setConsenting] = useState<Record<string, boolean>>({});
  
  // Conversation state for AI questions - track conversations per question ID
  const [conversations, setConversations] = useState<Record<string, Array<{role: 'user' | 'assistant', content: string, timestamp: Date}>>>({});
  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(null);
  const [conversationInput, setConversationInput] = useState<Record<string, string>>({});

  // Form state
  const [formData, setFormData] = useState<PolicyCreateEntry>({
    title: '',
    description: '',
    content: '',
    applicableProcesses: [],
    tags: [],
    priority: 0,
    isActive: true,
    extractedFromChat: false,
  });
  const [processInput, setProcessInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Load policies
  useEffect(() => {
    if (user?.id) {
      fetchPolicies();
    }
  }, [user?.id]);

  // Load consents and questions after policies are loaded
  useEffect(() => {
    if (isStaff && user?.id && policies.length > 0) {
      loadConsentsAndQuestions();
    }
  }, [isStaff, user?.id, policies.length]);

  const loadConsentsAndQuestions = async () => {
    if (!user?.id) return;
    
    try {
      // Load consents for all policies
      const consentsMap: Record<string, PolicyConsent> = {};
      for (const policy of policies) {
        try {
          const consent = await policyService.getPolicyConsent(policy.id, String(user.id));
          if (consent) {
            consentsMap[policy.id] = consent;
          }
        } catch (err) {
          // Ignore errors for individual consents
          console.warn(`Failed to load consent for policy ${policy.id}:`, err);
        }
      }
      setConsents(consentsMap);

      // Load questions for all policies
      const questionsMap: Record<string, PolicyQuestion[]> = {};
      try {
        const allQuestions = await policyService.getPolicyQuestions(undefined, String(user.id));
        for (const question of allQuestions) {
          if (!questionsMap[question.policyId]) {
            questionsMap[question.policyId] = [];
          }
          questionsMap[question.policyId].push(question);
        }
      } catch (err) {
        console.warn('Failed to load questions:', err);
      }
      setQuestions(questionsMap);
    } catch (err: any) {
      console.error('Error loading consents and questions:', err);
    }
  };

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await policyService.getPolicies();
      setPolicies(data);
    } catch (err: any) {
      console.error('Error fetching policies:', err);
      setError(err.message || 'Failed to load policies');
      notify(
        translate('pos.policies.load_failed', 'Failed to load policies'),
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.title || !formData.content || formData.applicableProcesses.length === 0) {
        notify(
          translate('pos.policies.validation_error', 'Please fill in all required fields'),
          { type: 'error' }
        );
        return;
      }

      await policyService.createPolicy(formData);
      notify(
        translate('pos.policies.created', 'Policy created successfully'),
        { type: 'success' }
      );
      setCreateDialogOpen(false);
      resetForm();
      fetchPolicies();
    } catch (err: any) {
      console.error('Error creating policy:', err);
      notify(
        translate('pos.policies.create_failed', 'Failed to create policy'),
        { type: 'error' }
      );
    }
  };

  const handleUpdate = async () => {
    if (!editingPolicy) return;

    try {
      if (!formData.title || !formData.content || formData.applicableProcesses.length === 0) {
        notify(
          translate('pos.policies.validation_error', 'Please fill in all required fields'),
          { type: 'error' }
        );
        return;
      }

      const updateData: PolicyUpdateEntry = {
        id: editingPolicy.id,
        ...formData,
      };
      await policyService.updatePolicy(editingPolicy.id, updateData);
      notify(
        translate('pos.policies.updated', 'Policy updated successfully'),
        { type: 'success' }
      );
      setEditDialogOpen(false);
      setEditingPolicy(null);
      resetForm();
      fetchPolicies();
    } catch (err: any) {
      console.error('Error updating policy:', err);
      notify(
        translate('pos.policies.update_failed', 'Failed to update policy'),
        { type: 'error' }
      );
    }
  };

  const handleDelete = async () => {
    if (!deletingPolicy) return;

    try {
      await policyService.deletePolicy(deletingPolicy.id);
      notify(
        translate('pos.policies.deleted', 'Policy deleted successfully'),
        { type: 'success' }
      );
      setDeleteDialogOpen(false);
      setDeletingPolicy(null);
      fetchPolicies();
    } catch (err: any) {
      console.error('Error deleting policy:', err);
      notify(
        translate('pos.policies.delete_failed', 'Failed to delete policy'),
        { type: 'error' }
      );
    }
  };

  const handleExtractFromChat = async () => {
    if (!user?.id) return;

    try {
      setExtracting(true);
      const history = await chatHistoryService.loadHistory(String(user.id));
      setChatHistory(history);
      
      if (!history || history.messages.length === 0) {
        notify(
          translate('pos.policies.no_chat_history', 'No chat history found'),
          { type: 'warning' }
        );
        return;
      }

      // Extract potential policies from chat messages
      // This is a simple extraction - in the future, this could use AI to identify policies
      const assistantMessages = history.messages.filter(
        (msg) => msg.role === 'assistant' && msg.content
      );

      if (assistantMessages.length === 0) {
        notify(
          translate('pos.policies.no_policies_in_chat', 'No policies found in chat history'),
          { type: 'warning' }
        );
        return;
      }

      // For now, we'll show the chat history and let the user manually create policies
      // In the future, this could be enhanced with AI extraction
      setExtractDialogOpen(true);
    } catch (err: any) {
      console.error('Error extracting from chat:', err);
      notify(
        translate('pos.policies.extract_failed', 'Failed to extract from chat'),
        { type: 'error' }
      );
    } finally {
      setExtracting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      applicableProcesses: [],
      tags: [],
      priority: 0,
      isActive: true,
      extractedFromChat: false,
    });
    setProcessInput('');
    setTagInput('');
    setRewrittenContent(null);
    setOriginalContent('');
    setRewriteImprovements([]);
  };

  const openCreateDialog = () => {
    resetForm();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (policy: Policy) => {
    setEditingPolicy(policy);
    setFormData({
      title: policy.title,
      description: policy.description || '',
      content: policy.content,
      applicableProcesses: policy.applicableProcesses || [],
      tags: policy.tags || [],
      priority: policy.priority || 0,
      isActive: policy.isActive,
      extractedFromChat: policy.extractedFromChat || false,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (policy: Policy) => {
    setDeletingPolicy(policy);
    setDeleteDialogOpen(true);
  };

  const addProcess = () => {
    if (processInput.trim()) {
      setFormData({
        ...formData,
        applicableProcesses: [...formData.applicableProcesses, processInput.trim()],
      });
      setProcessInput('');
    }
  };

  const removeProcess = (index: number) => {
    setFormData({
      ...formData,
      applicableProcesses: formData.applicableProcesses.filter((_, i) => i !== index),
    });
  };

  const addTag = () => {
    if (tagInput.trim()) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: (formData.tags || []).filter((_, i) => i !== index),
    });
  };

  const handleExtractMetadata = async () => {
    if (!formData.content.trim()) {
      notify(
        translate('pos.policies.no_content_for_extraction', 'Please enter policy content first'),
        { type: 'warning' }
      );
      return;
    }

    try {
      setExtractingMetadata(true);
      const metadata = await assistantService.extractPolicyMetadata(formData.content);
      
      // Populate form with extracted metadata
      setFormData({
        ...formData,
        title: metadata.title || formData.title,
        description: metadata.description || formData.description,
        applicableProcesses: metadata.applicableProcesses || formData.applicableProcesses,
        tags: metadata.tags || formData.tags,
        priority: metadata.priority !== undefined ? metadata.priority : formData.priority,
      });

      notify(
        translate('pos.policies.metadata_extracted', 'Policy metadata extracted successfully'),
        { type: 'success' }
      );
    } catch (err: any) {
      console.error('Error extracting metadata:', err);
      notify(
        translate('pos.policies.extract_metadata_failed', 'Failed to extract metadata: {{error}}', { error: err.message || 'Unknown error' }),
        { type: 'error' }
      );
    } finally {
      setExtractingMetadata(false);
    }
  };

  const handleRewriteContent = async () => {
    if (!formData.content.trim()) {
      notify(
        translate('pos.policies.no_content_for_rewrite', 'Please enter policy content first'),
        { type: 'warning' }
      );
      return;
    }

    try {
      setRewriting(true);
      // Detect language from content (simple heuristic - can be improved)
      const isVietnamese = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i.test(formData.content);
      const language = isVietnamese ? 'Vietnamese' : undefined;
      
      const result = await assistantService.rewritePolicyContent(formData.content, language);
      
      // Store original content and rewritten content
      setOriginalContent(formData.content);
      setRewrittenContent(result.rewrittenContent);
      setRewriteImprovements(result.improvements || []);
      
      notify(
        translate('pos.policies.content_rewritten', 'Content rewritten successfully. Review and approve or reject the changes.'),
        { type: 'success' }
      );
    } catch (err: any) {
      console.error('Error rewriting content:', err);
      notify(
        translate('pos.policies.rewrite_failed', 'Failed to rewrite content: {{error}}', { error: err.message || 'Unknown error' }),
        { type: 'error' }
      );
    } finally {
      setRewriting(false);
    }
  };

  const handleApproveRewrite = () => {
    if (rewrittenContent) {
      setFormData({
        ...formData,
        content: rewrittenContent,
      });
      setRewrittenContent(null);
      setOriginalContent('');
      setRewriteImprovements([]);
      notify(
        translate('pos.policies.rewrite_approved', 'Rewritten content approved and applied'),
        { type: 'success' }
      );
    }
  };

  const handleRejectRewrite = () => {
    setRewrittenContent(null);
    setOriginalContent('');
    setRewriteImprovements([]);
    notify(
      translate('pos.policies.rewrite_rejected', 'Rewritten content rejected'),
      { type: 'info' }
    );
  };

  const handleConsent = async (policy: Policy) => {
    if (!user?.id) return;
    
    try {
      setConsenting({ ...consenting, [policy.id]: true });
      const consent = await policyService.consentToPolicy({
        policyId: policy.id,
        userId: String(user.id),
        consentedAt: new Date(),
      });
      
      setConsents({ ...consents, [policy.id]: consent });
      notify(
        translate('pos.policies.consent_success', 'You have consented to this policy'),
        { type: 'success' }
      );
    } catch (err: any) {
      console.error('Error consenting to policy:', err);
      notify(
        translate('pos.policies.consent_failed', 'Failed to consent to policy'),
        { type: 'error' }
      );
    } finally {
      setConsenting({ ...consenting, [policy.id]: false });
    }
  };

  const handleAskQuestion = (policy: Policy, questionId?: string) => {
    setQuestioningPolicy(policy);
    setQuestionText('');
    setQuestionType(questionId ? 'AI' : 'MANAGER');
    setAskingAI(false);
    setCurrentQuestionId(questionId || null);
    setQuestionDialogOpen(true);
  };

  const handleSubmitQuestion = async () => {
    const inputText = currentQuestionId 
      ? (conversationInput[currentQuestionId] || '')
      : questionText;
    
    if (!questioningPolicy || !user?.id || !inputText.trim()) {
      notify(
        translate('pos.policies.question_required', 'Please enter a question'),
        { type: 'warning' }
      );
      return;
    }

    try {
      // If asking AI, get answer immediately
      if (questionType === 'AI') {
        setAskingAI(true);
        
        // Check if this is a follow-up question (conversation)
        const isFollowUp = currentQuestionId !== null;
        let conversationHistory: Array<{role: 'user' | 'assistant', content: string, timestamp: Date}> = [];
        
        if (isFollowUp && currentQuestionId) {
          // Get conversation history
          conversationHistory = conversations[currentQuestionId] || [];
          
          // Check message limit (count user messages)
          const userMessageCount = conversationHistory.filter(m => m.role === 'user').length;
          if (userMessageCount >= 10) {
            setAskingAI(false);
            notify(
              translate('pos.policies.max_messages_reached', 'You have reached the maximum number of messages (10). Please ask a manager directly for further assistance.'),
              { type: 'warning' }
            );
            return;
          }
        }
        
        // Construct context message
        let contextMessage = '';
        if (!isFollowUp) {
          // First question - include policy context
          contextMessage = `Policy: ${questioningPolicy.title}\n\n${questioningPolicy.description ? `Description: ${questioningPolicy.description}\n\n` : ''}Policy Content:\n${questioningPolicy.content}\n\nQuestion: ${inputText.trim()}`;
        } else {
          // Follow-up question - just the question
          contextMessage = inputText.trim();
        }
        
        // Build conversation history for API
        const history: Array<{role: 'user' | 'assistant', content: string}> = [];
        if (isFollowUp && conversationHistory.length > 0) {
          // Include previous conversation
          history.push(...conversationHistory.map(m => ({ role: m.role, content: m.content })));
        }
        history.push({ role: 'user', content: inputText.trim() });
        
        const aiResponse = await assistantService.chat({
          message: contextMessage,
          context: {
            page: 'policies',
            userId: String(user.id),
            locale: 'en',
          },
          history: history,
        });

        // Update conversation history
        const newMessagePair = [
          { role: 'user' as const, content: inputText.trim(), timestamp: new Date() },
          { role: 'assistant' as const, content: aiResponse.response, timestamp: new Date() },
        ];
        
        if (isFollowUp && currentQuestionId) {
          // Update existing conversation
          setConversations({
            ...conversations,
            [currentQuestionId]: [...(conversations[currentQuestionId] || []), ...newMessagePair],
          });
          
          // Update the question's answer with the latest response
          const updatedQuestions = { ...questions };
          if (updatedQuestions[questioningPolicy.id]) {
            const questionIndex = updatedQuestions[questioningPolicy.id].findIndex(q => q.id === currentQuestionId);
            if (questionIndex >= 0) {
              updatedQuestions[questioningPolicy.id][questionIndex] = {
                ...updatedQuestions[questioningPolicy.id][questionIndex],
                answer: aiResponse.response,
              };
              setQuestions(updatedQuestions);
            }
          }
          
          // Clear input for this conversation
          setConversationInput({ ...conversationInput, [currentQuestionId]: '' });
        } else {
          // Create new question with AI answer
          const question = await policyService.askQuestion({
            policyId: questioningPolicy.id,
            userId: String(user.id),
            question: inputText.trim(),
            askedAt: new Date(),
            isResolved: false, // Keep unresolved for follow-ups
            questionType: 'AI',
            answeredByAI: true,
            answer: aiResponse.response,
          });

          const policyQuestions = questions[questioningPolicy.id] || [];
          setQuestions({
            ...questions,
            [questioningPolicy.id]: [...policyQuestions, question],
          });
          
          // Initialize conversation
          setConversations({
            ...conversations,
            [question.id]: newMessagePair,
          });
          setCurrentQuestionId(question.id);
          setConversationInput({ ...conversationInput, [question.id]: '' });
        }

        if (!currentQuestionId) {
          setQuestionText('');
        }
        setAskingAI(false);
        
        if (!isFollowUp) {
          notify(
            translate('pos.policies.ai_answer_received', 'AI assistant has answered your question'),
            { type: 'success' }
          );
        }
      } else {
        // Submit to manager
        const question = await policyService.askQuestion({
          policyId: questioningPolicy.id,
          userId: String(user.id),
          question: inputText.trim(),
          askedAt: new Date(),
          isResolved: false,
          questionType: 'MANAGER',
        });

        const policyQuestions = questions[questioningPolicy.id] || [];
        setQuestions({
          ...questions,
          [questioningPolicy.id]: [...policyQuestions, question],
        });

        setQuestionDialogOpen(false);
        setQuestionText('');
        setQuestioningPolicy(null);
        setCurrentQuestionId(null);
        setConversationInput({});
        
        notify(
          translate('pos.policies.question_submitted', 'Your question has been submitted'),
          { type: 'success' }
        );
      }
    } catch (err: any) {
      console.error('Error submitting question:', err);
      setAskingAI(false);
      notify(
        translate('pos.policies.question_failed', 'Failed to submit question'),
        { type: 'error' }
      );
    }
  };

  // Filter policies
  const filteredPolicies = useMemo(() => {
    let filtered = policies;

    // Staff can only see active policies
    if (isStaff) {
      filtered = filtered.filter((p) => p.isActive === true);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          p.content.toLowerCase().includes(term) ||
          (p.description && p.description.toLowerCase().includes(term)) ||
          (p.tags && p.tags.some((tag) => tag.toLowerCase().includes(term))) ||
          (p.applicableProcesses && p.applicableProcesses.some((proc) => proc.toLowerCase().includes(term)))
      );
    }

    // Active filter (only for non-staff users)
    if (!isStaff && filterActive !== null) {
      filtered = filtered.filter((p) => p.isActive === filterActive);
    }

    return filtered;
  }, [policies, searchTerm, filterActive, isStaff]);

  if (loading) {
    return (
      <div className="space-y-6 py-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.policies.title', 'Policy Management')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.policies.subtitle',
              'Manage policies and extract them from chat history'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {canCreate && (
            <>
              <Button
                onClick={handleExtractFromChat}
                variant="outline"
                className="gap-2"
                disabled={extracting}
              >
                <MessageSquare className="h-4 w-4" />
                {extracting
                  ? translate('pos.policies.extracting', 'Extracting...')
                  : translate('pos.policies.extract_from_chat', 'Extract from Chat')}
              </Button>
              <Button onClick={openCreateDialog} variant="default" className="gap-2">
                <Plus className="h-4 w-4" />
                {translate('pos.policies.create', 'Create Policy')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-6 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.policies.total', 'Total Policies')}
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{policies.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.policies.active', 'Active')}
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {policies.filter((p) => p.isActive).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.policies.inactive', 'Inactive')}
            </CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {policies.filter((p) => !p.isActive).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.policies.from_chat', 'From Chat')}
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {policies.filter((p) => p.extractedFromChat).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={translate('pos.policies.search', 'Search policies...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {!isStaff && (
              <div className="flex gap-2">
                <Button
                  variant={filterActive === null ? 'default' : 'outline'}
                  onClick={() => setFilterActive(null)}
                >
                  {translate('pos.policies.all', 'All')}
                </Button>
                <Button
                  variant={filterActive === true ? 'default' : 'outline'}
                  onClick={() => setFilterActive(true)}
                >
                  {translate('pos.policies.active_only', 'Active')}
                </Button>
                <Button
                  variant={filterActive === false ? 'default' : 'outline'}
                  onClick={() => setFilterActive(false)}
                >
                  {translate('pos.policies.inactive_only', 'Inactive')}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Policies List */}
      {error && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-500">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {filteredPolicies.length === 0 ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {translate('pos.policies.no_policies', 'No Policies Found')}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {translate(
                    'pos.policies.create_first',
                    'Create your first policy or extract from chat history'
                  )}
                </p>
                {canCreate && (
                  <Button onClick={openCreateDialog} className="gap-2">
                    <Plus className="h-4 w-4" />
                    {translate('pos.policies.create', 'Create Policy')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredPolicies.map((policy) => (
            <Card key={policy.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle>{policy.title}</CardTitle>
                      {policy.isActive ? (
                        <Badge variant="default" className="bg-green-500">
                          {translate('pos.policies.active', 'Active')}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          {translate('pos.policies.inactive', 'Inactive')}
                        </Badge>
                      )}
                      {policy.extractedFromChat && (
                        <Badge variant="outline" className="gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {translate('pos.policies.from_chat', 'From Chat')}
                        </Badge>
                      )}
                    </div>
                    {policy.description && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {policy.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {policy.applicableProcesses?.map((proc, idx) => (
                        <Badge key={idx} variant="outline">
                          {proc}
                        </Badge>
                      ))}
                    </div>
                    {policy.tags && policy.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {policy.tags.map((tag, idx) => (
                          <Badge key={idx} variant="secondary" className="gap-1">
                            <Tag className="h-3 w-3" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  {(canEdit || canDelete) && (
                    <div className="flex gap-2">
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(policy)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(policy)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-code:text-foreground prose-pre:bg-muted prose-pre:text-foreground">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {policy.content}
                  </ReactMarkdown>
                </div>
                
                {/* Staff actions: Consent and Ask Question */}
                {isStaff && (
                  <div className="mt-4 pt-4 border-t flex gap-2">
                    {consents[policy.id] ? (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>{translate('pos.policies.consented', 'You have consented to this policy')}</span>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConsent(policy)}
                        disabled={consenting[policy.id]}
                        className="gap-2"
                      >
                        {consenting[policy.id] ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4" />
                        )}
                        {translate('pos.policies.consent', 'I Consent')}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAskQuestion(policy)}
                      className="gap-2"
                    >
                      <HelpCircle className="h-4 w-4" />
                      {translate('pos.policies.ask_question', 'Ask Question')}
                    </Button>
                  </div>
                )}
                
                {/* Display questions for this policy */}
                {isStaff && questions[policy.id] && questions[policy.id].length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold mb-2">
                      {translate('pos.policies.your_questions', 'Your Questions')}
                    </h4>
                    <div className="space-y-2">
                      {questions[policy.id].map((q) => (
                        <div key={q.id} className={`text-sm p-3 rounded-lg ${q.answeredByAI ? 'bg-blue-50 dark:bg-blue-950' : 'bg-muted'}`}>
                          <div className="flex items-start justify-between mb-1">
                            <div className="font-medium flex-1">{q.question}</div>
                            {q.answeredByAI && (
                              <Badge variant="outline" className="ml-2 gap-1">
                                <Sparkles className="h-3 w-3" />
                                {translate('pos.policies.ai_answer', 'AI')}
                              </Badge>
                            )}
                            {q.questionType === 'MANAGER' && !q.answer && (
                              <Badge variant="secondary" className="ml-2">
                                {translate('pos.policies.pending', 'Pending')}
                              </Badge>
                            )}
                          </div>
                          {/* Show conversation history for AI questions */}
                          {q.answeredByAI && conversations[q.id] && conversations[q.id].length > 0 && (
                            <div className="mt-2 space-y-2">
                              {conversations[q.id].map((msg, idx) => (
                                <div key={idx} className={`p-2 rounded text-xs ${msg.role === 'user' ? 'bg-background ml-4' : 'bg-blue-100 dark:bg-blue-900 mr-4'}`}>
                                  <div className="font-medium mb-1">
                                    {msg.role === 'user' ? translate('pos.policies.you', 'You') : translate('pos.policies.ai', 'AI')}:
                                  </div>
                                  <div className={msg.role === 'assistant' ? 'prose prose-xs max-w-none dark:prose-invert' : ''}>
                                    {msg.role === 'assistant' ? (
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {msg.content}
                                      </ReactMarkdown>
                                    ) : (
                                      msg.content
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Show single answer if no conversation history */}
                          {q.answer && (!q.answeredByAI || !conversations[q.id] || conversations[q.id].length === 0) && (
                            <div className="text-muted-foreground mt-2 pt-2 border-t">
                              <div className="font-medium mb-1 flex items-center gap-2">
                                {q.answeredByAI ? (
                                  <>
                                    <Sparkles className="h-3 w-3" />
                                    {translate('pos.policies.ai_answer_label', 'AI Answer')}:
                                  </>
                                ) : (
                                  <>
                                    <MessageSquare className="h-3 w-3" />
                                    {translate('pos.policies.answer', 'Answer')}:
                                  </>
                                )}
                              </div>
                              <div className="prose prose-sm max-w-none dark:prose-invert">
                                {q.answeredByAI ? (
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {q.answer}
                                  </ReactMarkdown>
                                ) : (
                                  q.answer
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Action buttons */}
                          <div className="mt-3 flex gap-2">
                            {q.answeredByAI && (
                              <>
                                {conversations[q.id] && conversations[q.id].filter(m => m.role === 'user').length < 10 ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleAskQuestion(policy, q.id)}
                                    className="gap-2"
                                  >
                                    <MessageSquare className="h-3 w-3" />
                                    {translate('pos.policies.continue_conversation', 'Continue Conversation')}
                                  </Button>
                                ) : (
                                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                                    <AlertCircle className="h-3 w-3" />
                                    {translate('pos.policies.max_messages_reached', 'Maximum messages reached. Please ask a manager directly.')}
                                  </div>
                                )}
                              </>
                            )}
                            {!q.answer && q.questionType === 'MANAGER' && (
                              <div className="text-muted-foreground text-xs">
                                {translate('pos.policies.pending_answer', 'Pending answer...')}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {translate('pos.policies.create', 'Create Policy')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.policies.create_description',
                'Create a new policy that can be used by the chatbot'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>
                {translate('pos.policies.title_label', 'Title')} *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder={translate('pos.policies.title_placeholder', 'Enter policy title')}
              />
            </div>
            <div>
              <Label>
                {translate('pos.policies.description_label', 'Description')}
              </Label>
              <Input
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={translate('pos.policies.description_placeholder', 'Enter description (optional)')}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  {translate('pos.policies.content_label', 'Content')} *
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRewriteContent}
                    disabled={rewriting || !formData.content.trim()}
                    className="gap-2"
                  >
                    {rewriting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translate('pos.policies.rewriting', 'Rewriting...')}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {translate('pos.policies.ai_rewrite', 'AI Rewrite')}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExtractMetadata}
                    disabled={extractingMetadata || !formData.content.trim()}
                    className="gap-2"
                  >
                    {extractingMetadata ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translate('pos.policies.extracting_metadata', 'Extracting...')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        {translate('pos.policies.ai_extract', 'AI Extract Metadata')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {rewrittenContent && (
                <div className="mb-4 p-4 bg-muted rounded-lg border-2 border-primary">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Label className="text-sm font-semibold mb-2 block">
                        {translate('pos.policies.rewritten_content', 'Rewritten Content')}
                      </Label>
                      {rewriteImprovements.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium mb-1">
                            {translate('pos.policies.improvements', 'Improvements:')}
                          </p>
                          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                            {rewriteImprovements.map((improvement, idx) => (
                              <li key={idx}>{improvement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleApproveRewrite}
                        className="gap-2"
                      >
                        <Check className="h-4 w-4" />
                        {translate('pos.policies.approve', 'Approve')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRejectRewrite}
                        className="gap-2"
                      >
                        <X className="h-4 w-4" />
                        {translate('pos.policies.reject', 'Reject')}
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={rewrittenContent}
                    readOnly
                    className="bg-background"
                    rows={6}
                  />
                </div>
              )}
              <Textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                placeholder={translate('pos.policies.content_placeholder', 'Enter policy content')}
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {translate('pos.policies.ai_extract_hint', 'Enter your policy content and click "AI Extract Metadata" to automatically fill title, description, processes, and tags')}
              </p>
            </div>
            <div>
              <Label>
                {translate('pos.policies.applicable_processes_label', 'Applicable Processes')} *
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={processInput}
                  onChange={(e) => setProcessInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addProcess();
                    }
                  }}
                  placeholder={translate('pos.policies.process_placeholder', 'Enter process name and press Enter')}
                />
                <Button type="button" onClick={addProcess}>
                  {translate('pos.policies.add', 'Add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.applicableProcesses.map((proc, idx) => (
                  <Badge key={idx} variant="default" className="gap-1">
                    {proc}
                    <button
                      onClick={() => removeProcess(idx)}
                      className="ml-1 hover:text-red-500"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>
                {translate('pos.policies.tags_label', 'Tags')}
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder={translate('pos.policies.tag_placeholder', 'Enter tag and press Enter')}
                />
                <Button type="button" onClick={addTag}>
                  {translate('pos.policies.add', 'Add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags?.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    <Tag className="h-3 w-3" />
                    {tag}
                    <button
                      onClick={() => removeTag(idx)}
                      className="ml-1 hover:text-red-500"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded"
                />
                <Label htmlFor="isActive">
                  {translate('pos.policies.is_active', 'Active')}
                </Label>
              </div>
              <div>
                <Label>
                  {translate('pos.policies.priority_label', 'Priority')}
                </Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-24"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleCreate}>
              {translate('pos.policies.create', 'Create Policy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {translate('pos.policies.edit', 'Edit Policy')}
            </DialogTitle>
            <DialogDescription>
              {translate('pos.policies.edit_description', 'Update policy information')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>
                {translate('pos.policies.title_label', 'Title')} *
              </Label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>
            <div>
              <Label>
                {translate('pos.policies.description_label', 'Description')}
              </Label>
              <Input
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>
                  {translate('pos.policies.content_label', 'Content')} *
                </Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRewriteContent}
                    disabled={rewriting || !formData.content.trim()}
                    className="gap-2"
                  >
                    {rewriting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translate('pos.policies.rewriting', 'Rewriting...')}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        {translate('pos.policies.ai_rewrite', 'AI Rewrite')}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleExtractMetadata}
                    disabled={extractingMetadata || !formData.content.trim()}
                    className="gap-2"
                  >
                    {extractingMetadata ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {translate('pos.policies.extracting_metadata', 'Extracting...')}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        {translate('pos.policies.ai_extract', 'AI Extract Metadata')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {rewrittenContent && (
                <div className="mb-4 p-4 bg-muted rounded-lg border-2 border-primary">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <Label className="text-sm font-semibold mb-2 block">
                        {translate('pos.policies.rewritten_content', 'Rewritten Content')}
                      </Label>
                      {rewriteImprovements.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-medium mb-1">
                            {translate('pos.policies.improvements', 'Improvements:')}
                          </p>
                          <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                            {rewriteImprovements.map((improvement, idx) => (
                              <li key={idx}>{improvement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={handleApproveRewrite}
                        className="gap-2"
                      >
                        <Check className="h-4 w-4" />
                        {translate('pos.policies.approve', 'Approve')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRejectRewrite}
                        className="gap-2"
                      >
                        <X className="h-4 w-4" />
                        {translate('pos.policies.reject', 'Reject')}
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={rewrittenContent}
                    readOnly
                    className="bg-background"
                    rows={6}
                  />
                </div>
              )}
              <Textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                rows={6}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {translate('pos.policies.ai_extract_hint', 'Enter your policy content and click "AI Extract Metadata" to automatically fill title, description, processes, and tags')}
              </p>
            </div>
            <div>
              <Label>
                {translate('pos.policies.applicable_processes_label', 'Applicable Processes')} *
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={processInput}
                  onChange={(e) => setProcessInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addProcess();
                    }
                  }}
                />
                <Button type="button" onClick={addProcess}>
                  {translate('pos.policies.add', 'Add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.applicableProcesses.map((proc, idx) => (
                  <Badge key={idx} variant="default" className="gap-1">
                    {proc}
                    <button
                      onClick={() => removeProcess(idx)}
                      className="ml-1 hover:text-red-500"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <Label>
                {translate('pos.policies.tags_label', 'Tags')}
              </Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                />
                <Button type="button" onClick={addTag}>
                  {translate('pos.policies.add', 'Add')}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags?.map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="gap-1">
                    <Tag className="h-3 w-3" />
                    {tag}
                    <button
                      onClick={() => removeTag(idx)}
                      className="ml-1 hover:text-red-500"
                    >
                      <XCircle className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                  className="rounded"
                />
                <Label htmlFor="isActiveEdit">
                  {translate('pos.policies.is_active', 'Active')}
                </Label>
              </div>
              <div>
                <Label>
                  {translate('pos.policies.priority_label', 'Priority')}
                </Label>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      priority: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-24"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleUpdate}>
              {translate('pos.policies.update', 'Update Policy')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.policies.delete', 'Delete Policy')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.policies.delete_confirmation',
                `Are you sure you want to delete "${deletingPolicy?.title}"? This action cannot be undone.`
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('pos.policies.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extract from Chat Dialog */}
      <Dialog open={extractDialogOpen} onOpenChange={setExtractDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {translate('pos.policies.extract_from_chat', 'Extract Policies from Chat')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.policies.extract_description',
                'Review chat history and create policies from assistant messages'
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {chatHistory && chatHistory.messages.length > 0 ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {translate(
                    'pos.policies.chat_messages_count',
                    `Found ${chatHistory.messages.length} messages in chat history`
                  )}
                </p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {chatHistory.messages
                    .filter((msg) => msg.role === 'assistant' && msg.content)
                    .map((msg, idx) => (
                      <Card key={idx}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm whitespace-pre-wrap">
                                {msg.content}
                              </p>
                              {msg.timestamp && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  {new Date(msg.timestamp).toLocaleString()}
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setFormData({
                                  title: `Policy from Chat ${idx + 1}`,
                                  description: '',
                                  content: msg.content || '',
                                  applicableProcesses: [],
                                  tags: ['extracted'],
                                  priority: 0,
                                  isActive: true,
                                  extractedFromChat: true,
                                  sourceChatTimestamp: msg.timestamp,
                                });
                                setExtractDialogOpen(false);
                                setCreateDialogOpen(true);
                              }}
                            >
                              {translate('pos.policies.use_as_policy', 'Use as Policy')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">
                {translate('pos.policies.no_chat_history', 'No chat history found')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setExtractDialogOpen(false)}
            >
              {translate('pos.common.action.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ask Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.policies.ask_question', 'Ask Question')}
            </DialogTitle>
            <DialogDescription>
              {questioningPolicy && (
                <span>
                  {translate('pos.policies.ask_question_desc', 'Ask a question about: {{title}}', { title: questioningPolicy.title })}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>
                {translate('pos.policies.question_type', 'Question Type')}
              </Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={questionType === 'AI' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuestionType('AI')}
                  className="flex-1"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  {translate('pos.policies.ask_ai', 'Ask AI Assistant')}
                </Button>
                <Button
                  type="button"
                  variant={questionType === 'MANAGER' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setQuestionType('MANAGER')}
                  className="flex-1"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {translate('pos.policies.ask_manager', 'Ask Manager')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {questionType === 'AI' 
                  ? translate('pos.policies.ai_desc', 'Get an instant answer from the AI assistant')
                  : translate('pos.policies.manager_desc', 'Submit your question for a manager to answer')
                }
              </p>
            </div>
            <div>
              <Label htmlFor="question">
                {translate('pos.policies.question', 'Question')} *
              </Label>
              <Textarea
                id="question"
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder={translate('pos.policies.question_placeholder', 'Enter your question about this policy...')}
                rows={4}
                disabled={askingAI}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setQuestionDialogOpen(false);
                setQuestionText('');
                setQuestioningPolicy(null);
                setCurrentQuestionId(null);
                setAskingAI(false);
                setConversationInput({});
              }}
              disabled={askingAI}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleSubmitQuestion} 
              disabled={
                askingAI || 
                Boolean(currentQuestionId 
                  ? !conversationInput[currentQuestionId]?.trim() 
                  : !questionText.trim()) ||
                Boolean(currentQuestionId && conversations[currentQuestionId]?.filter(m => m.role === 'user').length >= 10)
              }
            >
              {askingAI ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {translate('pos.policies.asking_ai', 'Asking AI...')}
                </>
              ) : questionType === 'AI' ? (
                currentQuestionId ? translate('pos.policies.send_message', 'Send Message') : translate('pos.policies.ask_ai', 'Ask AI Assistant')
              ) : (
                translate('pos.policies.submit_question', 'Submit Question')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernPolicyManagement;

